import { apiFetch } from './http';
import { apiBaseUrl } from './baseUrl';
import { retryAfterSeconds } from './retryAfter';

// ─────────────────────────────────────────────────────────────────────
// REGISTERING, AND ASKING A CLUB TO LET YOU IN — the browser half (#710).
//
// Three calls and one credential. `register/start` and `register/verify` are
// unauthenticated, because this is how identity is OBTAINED; `join-request`
// carries the onboarding grant (`spo_`) that `verify` hands back.
//
// ── THE GRANT DOES NOT GO THROUGH `apiFetch`, ON PURPOSE ───────────
//
// `apiFetch` attaches whatever the club or member session holds, to every
// request that goes through it. If the grant were added to that list it could
// be attached to a club or member call by accident, and the whole argument for
// a fourth credential family is that it reaches EXACTLY ONE endpoint
// (`apps/api/src/auth/onboardingMiddleware.ts`).
//
// So the one call that uses it passes it explicitly, from the caller, and this
// module never stores it. `clubSession` and `memberSession` do their own fetch
// for the same reason — a credential's own establishment is the one place the
// shared transport is the wrong tool.
//
// WHERE IT IS KEPT: in memory, by `lib/auth/onboarding.ts`. Not in a cookie —
// the API refuses a grant that arrives as one — and not in storage, because a
// thirty-minute door does not need to survive a reload. If somebody reloads
// mid-journey they ask for another code, which costs them one e-mail.
//
// ── THE SCREEN MUST NOT KNOW MORE THAN THE SERVER SAYS ─────────────
//
// `register/start` answers the same 202 whether the address was new, already
// had an account, or is known under another provider — so the types below
// cannot express those differences. A registration form that said «this address
// is already registered» is a membership list anybody can read.
// ─────────────────────────────────────────────────────────────────────

/** Mirrors `ONBOARDING_GRANT_TOKEN_PREFIX`. A type tag, not a secret. */
export const ONBOARDING_GRANT_PREFIX = 'spo_';

export type RegisterStartOutcome =
  /** The server took it. Nothing more — see the header. */
  | { status: 'accepted' }
  /** The address is not shaped like one, or the name was blank. Safe to say. */
  | { status: 'invalid'; field: 'email' | 'name' }
  | { status: 'not_configured' }
  | { status: 'throttled'; retryAfterSeconds: number }
  | { status: 'unreachable' };

export async function startRegistration(input: {
  email: string;
  name: string;
}): Promise<RegisterStartOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/auth/member/register/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, name: input.name }),
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 503) return { status: 'not_configured' };
  if (res.status === 429) return { status: 'throttled', retryAfterSeconds: retryAfterSeconds(res) };
  if (res.status === 400) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    // The two the server distinguishes, and nothing else is inferred: an
    // unrecognised 400 is reported as the address, which is the field people
    // mistype.
    return { status: 'invalid', field: body?.error === 'name_required' ? 'name' : 'email' };
  }
  if (res.ok) return { status: 'accepted' };
  return { status: 'unreachable' };
}

/** What a verified mailbox gets you: a grant, and the player you now are. */
export interface Registered {
  grant: string;
  expiresAt: string;
  player: { id: string; name: string };
}

export type RegisterVerifyOutcome =
  | ({ status: 'registered' } & Registered)
  /**
   * ONE REFUSAL FOR EVERY FAILURE, like the sign-in door. A wrong code, an
   * expired one, an address that never started a registration and a spent
   * attempt budget are one answer, and no type here can say which.
   */
  | { status: 'refused' }
  /**
   * A 200 whose body is not the contract — see the note on the join outcome.
   *
   * SEPARATE FROM `refused`, which it used to be folded into: «wrong or expired
   * code» told somebody to re-read their e-mail when the code was fine and the
   * response was broken. The refusal path above is unaffected — a real refusal
   * never reaches here, because it is not a 2xx.
   */
  | { status: 'protocol' }
  | { status: 'not_configured' }
  | { status: 'throttled'; retryAfterSeconds: number }
  | { status: 'unreachable' };

export async function verifyRegistration(input: {
  email: string;
  code: string;
}): Promise<RegisterVerifyOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/auth/member/register/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, code: input.code }),
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 503) return { status: 'not_configured' };
  if (res.status === 429) return { status: 'throttled', retryAfterSeconds: retryAfterSeconds(res) };
  if (!res.ok) return { status: 'refused' };

  const body = (await res.json().catch(() => null)) as Partial<Registered> | null;
  const player = body?.player;
  // FAILS CLOSED ON A BODY IT CANNOT READ. A 200 whose grant is missing is not a
  // registration — treating it as one would leave a screen believing somebody is
  // onboarded with no credential to act with.
  if (
    typeof body?.grant !== 'string' ||
    !body.grant.startsWith(ONBOARDING_GRANT_PREFIX) ||
    typeof player?.id !== 'string' ||
    typeof player?.name !== 'string'
  ) {
    return { status: 'protocol' };
  }
  return {
    status: 'registered',
    grant: body.grant,
    expiresAt: typeof body.expiresAt === 'string' ? body.expiresAt : '',
    player: { id: player.id, name: player.name },
  };
}

export type JoinRequestOutcome =
  /** Created now, or already pending — both mean «the club has it». */
  | { status: 'pending'; requestedAt: string; club: { slug: string; name: string } }
  | { status: 'already_member' }
  | { status: 'unknown_club' }
  /** A decision said never. The club has to invite them. */
  | { status: 'blocked' }
  /** A decision said «not before this date». */
  | { status: 'not_yet'; from: string }
  /** A decided request with no recorded policy — the club has to be asked. */
  | { status: 'closed' }
  /**
   * Too many outstanding requests (`too_many_pending`). A standing cap that
   * does not clear with time, so there is no wait to show; the field is kept
   * at 0 for the callers that read it.
   */
  | { status: 'too_many'; retryAfterSeconds: number }
  /**
   * The rate budget is spent (`too_many_attempts`): a wait, in whole seconds,
   * 0 when it could not be read (#778 Q7). Its own status, because «0» must not
   * be read as «a standing cap» — it was, whenever the header was unreadable.
   */
  | { status: 'throttled'; retryAfterSeconds: number }
  /** The grant is gone — expired, or superseded by a newer one. */
  | { status: 'expired' }
  /**
   * The server answered, and the answer was not the contract.
   *
   * ITS OWN OUTCOME RATHER THAN `pending` (#733 review). Treating any 2xx as
   * «sent» means a malformed response shows «søknaden er sendt» for a request
   * nothing may have recorded — the one sentence this journey must never say
   * falsely, because the person then waits for an approval that is not coming.
   *
   * And not `unreachable` either: we DID reach the server, and saying otherwise
   * would send somebody to check their wifi.
   */
  | { status: 'protocol' }
  | { status: 'unreachable' };

/**
 * Ask a club to let this grant's holder in.
 *
 * `grant` is passed rather than read from anywhere, which is the whole point —
 * see the header. This is the only function in the web app that sends it.
 */
export async function requestToJoinClub(input: {
  clubSlug: string;
  grant: string;
}): Promise<JoinRequestOutcome> {
  let res: Response;
  try {
    res = await fetch(
      `${apiBaseUrl()}/onboarding/clubs/${encodeURIComponent(input.clubSlug)}/join-request`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${input.grant}` },
      },
    );
  } catch {
    return { status: 'unreachable' };
  }

  if (res.status === 401) return { status: 'expired' };
  if (res.status === 404) return { status: 'unknown_club' };
  if (res.status === 429) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    // `too_many_pending` and `too_many_attempts` are both «not now», and only
    // the second has a wait. Anything else is the rate budget's answer.
    return body?.error === 'too_many_pending'
      ? { status: 'too_many', retryAfterSeconds: 0 }
      : { status: 'throttled', retryAfterSeconds: retryAfterSeconds(res) };
  }
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as { error?: string; from?: string } | null;
    if (body?.error === 'already_member') return { status: 'already_member' };
    if (body?.error === 'reapply_blocked') return { status: 'blocked' };
    if (body?.error === 'reapply_not_yet') {
      return { status: 'not_yet', from: typeof body.from === 'string' ? body.from : '' };
    }
    return { status: 'closed' };
  }
  if (!res.ok) return { status: 'unreachable' };

  // ── FAILS CLOSED ON A BODY IT CANNOT READ (#733 review) ───────────
  //
  // The first version accepted any 2xx and filled the gaps — the slug echoed
  // back from the request, an empty date. That shows «søknaden er sendt» for a
  // response that never established the contract, and the person then waits for
  // an approval nobody is coming to give. Substituting the caller's OWN input
  // for the server's answer is the part that makes it convincing: the screen
  // names the club because the caller named it, not because the server did.
  //
  // So every field the screen uses is checked, and anything else is a protocol
  // error. `verifyRegistration` above has the same shape for the same reason.
  const body = (await res.json().catch(() => null)) as {
    status?: unknown;
    requestedAt?: unknown;
    club?: { slug?: unknown; name?: unknown };
  } | null;
  const requestedAt = typeof body?.requestedAt === 'string' ? body.requestedAt : '';
  const slug = typeof body?.club?.slug === 'string' ? body.club.slug : '';
  const name = typeof body?.club?.name === 'string' ? body.club.name : '';
  if (
    body?.status !== 'pending' ||
    !Number.isFinite(Date.parse(requestedAt)) ||
    slug === '' ||
    name === ''
  ) {
    return { status: 'protocol' };
  }
  return { status: 'pending', requestedAt, club: { slug, name } };
}
